/**
 * The COMMITTED section scope: which non-core section, if any, is mounted in
 * this tab (spec customer-success/v1.5.1/002 §2.1; FIX-SCOPE-STORE).
 *
 * It is no longer how a request learns its scope. It used to be: every RPC
 * layer read it per request, and `PlatformScopeProvider` wrote it DURING
 * RENDER. Both halves leaked (`__tests__/scope-store-flicker.repro.test.tsx`):
 * a root-layout widget polling while `/cs` was open sent the section's claim
 * and cached the answer under its scope-free key (A), and a section render
 * React threw away left the value set with no unmount to reset it (B). A
 * request now carries the scope of the tree that made it: the query runner
 * reads it off the `QueryClient` (`query-client-scope.ts`) and hands it to
 * `AppHttpClient` on the fiber (`withRequestScope`); a raw `fetch` caller
 * passes it (`fetchWithScope`).
 *
 * What is left here is a commit-time fact for the readers that are not
 * requests and are not in React: the per-scope storage partition
 * (`use-connection-filter.ts`) and `AppHttpClient`'s fallback for an Effect run
 * that named no scope. ONE WRITER: `PlatformScopeProvider`, in a layout effect
 * (so never for a render React discards), reset in that effect's cleanup.
 * Ratcheted by `src/__tests__/boundaries/platform-scope-provider.test.ts`.
 *
 * The default is `insurance-broker`, which is also what an absent header means
 * on the server (S3).
 */
import { DEFAULT_PLATFORM_SCOPE, type PlatformScope } from "@/domain/scope/platform-scope"

let current: PlatformScope = DEFAULT_PLATFORM_SCOPE

/** Readers that must hear a change (`useSyncExternalStore`). Still ONE writer. */
const listeners = new Set<() => void>()

/** The committed section scope: the default unless a section provider is mounted. */
export const getCurrentScope = (): PlatformScope => current

/** Subscribe to scope changes (`useSyncExternalStore`). Returns the unsubscribe. */
export const subscribeCurrentScope = (listener: () => void): (() => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/**
 * Notified on a microtask rather than synchronously: the writer runs in a
 * layout effect, and a synchronous notification would schedule a subscriber's
 * update inside that commit. `useSyncExternalStore` re-reads `getSnapshot()` on
 * every render, so the delay cannot show a stale value.
 */
let publishScheduled = false

const publish = (): void => {
  if (publishScheduled) return
  publishScheduled = true
  queueMicrotask(() => {
    publishScheduled = false
    for (const listener of listeners) listener()
  })
}

/**
 * Enter a scope. Called by `PlatformScopeProvider` from a LAYOUT effect, which
 * runs before any passive effect in the tree, so a child's `useEffect` (the
 * connection-scope hydration) already reads the section's partition. Never
 * from render: a render React discards must not leave it set.
 */
export const setCurrentScope = (scope: PlatformScope): void => {
  if (current === scope) return
  current = scope
  publish()
}

/** Back to the broker default — the provider's unmount cleanup. */
export const resetCurrentScope = (): void => {
  if (current === DEFAULT_PLATFORM_SCOPE) return
  current = DEFAULT_PLATFORM_SCOPE
  publish()
}
